import { useContext } from 'react';
import { Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemedLoadingAnimation } from './themed';
import { VenueContext } from '../../contexts/VenueContext';
import type { VenueTypeCode } from '../../features/landing/constants/venueTypes';

const VENUE_TYPE_HINT_KEY = 'lov-venue-type-hint';

/**
 * Reads the venue type for the currently-selected venue from context (best effort).
 * Falls back to the localStorage hint written by VenueContext once venues load.
 * Returns undefined if neither source has data (e.g. first load before any API call).
 */
function useVenueTypeHint(): VenueTypeCode | undefined {
  // Use useContext directly (no throw) so this works outside VenueProvider in tests
  const ctx = useContext(VenueContext);

  if (ctx) {
    const { venues, selectedVenueId } = ctx;
    if (venues.length > 0) {
      const selected =
        selectedVenueId !== 'all' ? venues.find((v) => v.id === selectedVenueId) : null;
      const venueType = selected?.venueType ?? venues[0]?.venueType;
      if (venueType) return venueType as VenueTypeCode;
    }
  }

  // Best-effort localStorage fallback for Suspense boundaries rendered before
  // VenueContext has populated (or when context is unavailable in tests).
  try {
    const hint = localStorage.getItem(VENUE_TYPE_HINT_KEY);
    if (hint) return hint as VenueTypeCode;
  } catch {
    // Ignore storage errors
  }

  return undefined;
}

export const VendorLoadingFallback = () => {
  const venueType = useVenueTypeHint();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
      }}
    >
      {/*
       * AnimatePresence + motion.div provides a smooth fade-in entrance.
       * The exit animation (opacity: 0) fires when a persistent AnimatePresence
       * ancestor removes this node — e.g. if the Suspense boundary is wrapped
       * with AnimatePresence in the future. Without such a parent the exit prop
       * is a no-op but the entrance animation always works correctly.
       */}
      <AnimatePresence>
        <motion.div
          key="vendor-loading-fallback"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <ThemedLoadingAnimation venueType={venueType} />
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};
